/**
 * Add/Edit dialog for an Arrhenius maturity equation (group).
 *
 * Mirrors the legacy MaturityArrheniusAdd/Edit forms: Activation Energy <-> Q
 * (÷8.31) and Specified Temperature °C <-> °F both convert live. The name
 * (Group ID and description) can always be edited; a locked group (one a
 * finished calibration or started pour uses) keeps its values, so those
 * fields are read-only.
 * Values the server refuses (activation energy not a whole number above 0,
 * specified temperature outside 0..50 °C) block Save; values outside the
 * typical ranges (30000..60000 J/mol, Ts other than 20 or 23 °C) only warn
 * (inputRanges).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Lock as LockIcon } from '@mui/icons-material';
import { cToF, fToC, activationEnergyToQ, qToActivationEnergy } from './tempConvert';
import {
  ACTIVATION_ENERGY_MAX,
  ACTIVATION_ENERGY_TYPICAL,
  Q_TYPICAL,
  SPECIFIED_TEMP_LIMITS,
  activationEnergyLevel,
  parseNumber,
  specifiedTempLevel,
} from './inputRanges';
import type { ArrheniusTarget } from '../../services/maturity';

export interface ArrheniusFormValues {
  group_id: string;
  group_description: string;
  activation_energy: string;
  specified_temp: string; // °C, held as a string
}

interface Props {
  open: boolean;
  editing: ArrheniusTarget | null;
  /** Optional pre-filled values when adding (e.g. from the datum/Q workbench). */
  initialValues?: Partial<ArrheniusFormValues>;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: ArrheniusFormValues) => void;
}

const EMPTY: ArrheniusFormValues = {
  group_id: '',
  group_description: '',
  activation_energy: '',
  specified_temp: '',
};

const ArrheniusDialog: React.FC<Props> = ({ open, editing, initialValues, saving, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<ArrheniusFormValues>(EMPTY);
  const [tempF, setTempF] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [errors, setErrors] = useState<{ group_id?: boolean; activation_energy?: boolean; specified_temp?: boolean }>({});

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const c = String(editing.specified_temp ?? '');
      const ae = String(editing.activation_energy ?? '');
      setForm({
        group_id: editing.group_id,
        group_description: editing.group_description ?? '',
        activation_energy: ae,
        specified_temp: c,
      });
      setTempF(cToF(c));
      setQ(activationEnergyToQ(ae));
    } else {
      const seeded = { ...EMPTY, ...(initialValues ?? {}) };
      setForm(seeded);
      setTempF(cToF(seeded.specified_temp));
      setQ(activationEnergyToQ(seeded.activation_energy));
    }
    setErrors({});
  }, [open, editing, initialValues]);

  const isEdit = editing !== null;
  const valuesLocked = !!editing?.is_locked;

  const handleActivationEnergy = (value: string) => {
    setForm((prev) => ({ ...prev, activation_energy: value }));
    setQ(activationEnergyToQ(value));
  };

  const handleQ = (value: string) => {
    setQ(value);
    setForm((prev) => ({ ...prev, activation_energy: qToActivationEnergy(value) }));
  };

  const handleCelsius = (value: string) => {
    setForm((prev) => ({ ...prev, specified_temp: value }));
    setTempF(cToF(value));
  };

  const handleFahrenheit = (value: string) => {
    setTempF(value);
    setForm((prev) => ({ ...prev, specified_temp: fToC(value) }));
  };

  // Out of range is shown as soon as a number is typed; blank only after Save.
  // The flags Save sets are used only while a field is blank, so a value typed
  // after a refused Save gets its own error or warning.
  const aeValue = parseNumber(form.activation_energy);
  const aeLevel = activationEnergyLevel(form.activation_energy);
  const aeError = aeValue === null ? !!errors.activation_energy : aeLevel === 'invalid';
  const aeUnusual = !aeError && aeLevel === 'unusual';
  let aeHelper: string | undefined;
  if (aeValue === null) {
    aeHelper = errors.activation_energy
      ? t('maturityTarget.activationEnergyRequired', 'A valid activation energy is required')
      : undefined;
  } else if (aeValue > ACTIVATION_ENERGY_MAX) {
    aeHelper = t('maturityTarget.activationEnergyTooHigh', {
      defaultValue: 'Activation energy must be no more than {{max}} J/mol.',
      max: ACTIVATION_ENERGY_MAX,
    });
  } else if (aeLevel === 'invalid') {
    aeHelper = t('maturityTarget.activationEnergyInvalid', 'Enter a whole number of J/mol greater than 0 (for example 33500).');
  } else if (aeUnusual) {
    aeHelper = t('maturityTarget.activationEnergyUnusual', {
      defaultValue:
        'Unusual value: activation energies are usually {{min}} to {{max}} J/mol (Q {{qMin}} to {{qMax}} K). Check it before saving.',
      min: ACTIVATION_ENERGY_TYPICAL.min,
      max: ACTIVATION_ENERGY_TYPICAL.max,
      qMin: Q_TYPICAL.min,
      qMax: Q_TYPICAL.max,
    });
  }

  const tsBlank = parseNumber(form.specified_temp) === null;
  const tsLevel = specifiedTempLevel(form.specified_temp);
  const tsError = tsBlank ? !!errors.specified_temp : tsLevel === 'invalid';
  const tsUnusual = !tsError && tsLevel === 'unusual';
  let tsHelper: string | undefined;
  if (tsBlank) {
    tsHelper = errors.specified_temp ? t('maturityTarget.tempRequired', 'A valid temperature is required') : undefined;
  } else if (tsLevel === 'invalid') {
    tsHelper = t('maturityTarget.specifiedTempInvalid', {
      defaultValue: 'Enter a specified temperature between {{min}} and {{max}} °C.',
      min: SPECIFIED_TEMP_LIMITS.min,
      max: SPECIFIED_TEMP_LIMITS.max,
    });
  } else if (tsUnusual) {
    tsHelper = t(
      'maturityTarget.specifiedTempUnusual',
      'Unusual value: the specified temperature is usually 20 or 23 °C. Check it before saving.',
    );
  }

  const handleSubmit = () => {
    const nextErrors = {
      group_id: !form.group_id.trim(),
      activation_energy: !valuesLocked && activationEnergyLevel(form.activation_energy) === 'invalid',
      specified_temp: !valuesLocked && specifiedTempLevel(form.specified_temp) === 'invalid',
    };
    setErrors(nextErrors);
    if (nextErrors.group_id || nextErrors.activation_energy || nextErrors.specified_temp) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? t('maturityTarget.editArrhenius', 'Edit Arrhenius Equation')
          : t('maturityTarget.addArrhenius', 'Add Arrhenius Equation')}
      </DialogTitle>
      <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 3 } }}>
        {valuesLocked && (
          <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }} data-testid="values-locked">
            {t('maturityTarget.valuesLocked', {
              defaultValue: '{{reason}} You can still rename it, or use "Duplicate as new" for different values.',
              reason: editing?.lock_reason || t('maturityTarget.locked', 'Locked'),
            })}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              label={t('maturityTarget.groupId', 'Group ID')}
              value={form.group_id}
              onChange={(e) => setForm((p) => ({ ...p, group_id: e.target.value }))}
              error={errors.group_id}
              helperText={errors.group_id ? t('maturityTarget.groupIdRequired', 'Group ID is required') : undefined}
              inputProps={{ maxLength: 50 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={t('maturityTarget.groupDescription', 'Group Description')}
              value={form.group_description}
              onChange={(e) => setForm((p) => ({ ...p, group_description: e.target.value }))}
              inputProps={{ maxLength: 50 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label={t('maturityTarget.activationEnergy', 'Activation Energy')}
              value={form.activation_energy}
              onChange={(e) => handleActivationEnergy(e.target.value)}
              disabled={valuesLocked}
              error={aeError}
              helperText={aeHelper}
              InputProps={{ endAdornment: <InputAdornment position="end">J/mol</InputAdornment> }}
              slotProps={{
                inputLabel: { shrink: true },
                formHelperText: aeUnusual ? { sx: { color: 'warning.dark' } } : undefined,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('maturityTarget.q', 'Q')}
              value={q}
              onChange={(e) => handleQ(e.target.value)}
              disabled={valuesLocked}
              helperText={t('maturityTarget.qHint', 'Q = Activation Energy / 8.31')}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label={t('maturityTarget.specifiedTemp', 'Specified Temperature')}
              value={form.specified_temp}
              onChange={(e) => handleCelsius(e.target.value)}
              disabled={valuesLocked}
              error={tsError}
              helperText={tsHelper}
              InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
              slotProps={{
                inputLabel: { shrink: true },
                formHelperText: tsUnusual ? { sx: { color: 'warning.dark' } } : undefined,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('maturityTarget.specifiedTemp', 'Specified Temperature')}
              value={tempF}
              onChange={(e) => handleFahrenheit(e.target.value)}
              disabled={valuesLocked}
              InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        >
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArrheniusDialog;
